import { Form } from "antd";
import { GoogleColored } from "assets/icons";
import BrandLogo from "assets/images/svg/click-for-marry02.svg";
import Button from "components/BaseWidgets/button";
import Divider from "components/BaseWidgets/divider";
import Input from "components/BaseWidgets/form/input";
import Password from "components/BaseWidgets/form/password";
import RadioButton from "components/BaseWidgets/radioButton";
import Select from "components/BaseWidgets/select";
import AuthLayout from "../components/authLayout";
import "./style.scss";
import { useLogin, useGoogleLogin } from "hooks/api/useAuth";
import { useEffect, useState } from "react";
import { clearStorage } from "utils/storage";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useSelectCode } from "hooks/api/useListing";
import { requestForToken } from "../../../firebase/firebase";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { storeToken } from "utils/storage";
import dayjs from "dayjs";
import { useAuth } from "context/AuthContext";

function Login() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loginMethod, setLoginMethod] = useState("phone");
  const [countryCode, setCountryCode] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);

  const { mutate: loginApi, isPending } = useLogin();
  const { mutate: googleLoginMutation } = useGoogleLogin();

  useEffect(() => {
    requestForToken().then((token) => setFcmToken(token));
    clearStorage();
  }, []);

  const { data: countrylist } = useSelectCode({ search_key: "" });

  const countryCodesOptions = countrylist?.country_codes?.map((item) => ({
    value: item.id,
    label: item.code,
    phone_length: item.phone_length,
  }));

  useEffect(() => {
    if (countryCodesOptions && !countryCode) {
      const defaultCode = countryCodesOptions.find((c) => c.label === "+91");
      if (defaultCode) setCountryCode(defaultCode);
    }
  }, [countryCodesOptions, countryCode]);

  const onFinish = (values) => {
    let identifier = values.identifier || "";

    const formField = {
      identifier,
      password: values.password || "",
    };

    if (loginMethod === "phone" && countryCode) {
      let phoneNumber = identifier.replace(/\D/g, "");
      const codeWithoutPlus = countryCode.label.replace("+", "");

      if (phoneNumber.startsWith(codeWithoutPlus)) {
        phoneNumber = phoneNumber.substring(codeWithoutPlus.length);
      }

      formField.identifier = phoneNumber;
      formField.countrycode = countryCode.value;
    }

    loginApi({ payload: formField, fcmToken });
  };

  const handleGoogleLogin = (credential) => {
    try {
      const decoded = jwtDecode(credential);
      googleLoginMutation(
        { token: credential },
        {
          onSuccess: (data) => {
            const userData = data?.data?.user;
            if (data?.data?.is_new_user) {
              storeToken("accessToken", data.data.access);
              storeToken("deviceId", data.data.device_id);
              AlertNotification("success", "User registered");
              navigate("/create#google_auth", {
                state: { name: decoded?.name },
              });
              return;
            }
            if (!userData) {
              AlertNotification("error", "User data missing in response");
              return;
            }
            storeToken("accessToken", data?.data?.access);
            storeToken("refreshToken", data?.data?.refresh);
            storeToken("deviceId", data?.data?.device_id);
            storeToken("onboarding_steps", userData?.onboarding_steps);
            storeToken("gopremiummodal", userData?.is_premium_member);

            if (data?.data?.access_expires) {
              const expiry = dayjs.unix(data.data.access_expires).toISOString();
              storeToken("tokenExpiryTime", expiry);
            }
            login(userData);
            if (userData?.is_premium_member) {
              storeToken("isPremium", "true");
            }
            AlertNotification("success", data.message);

            const step = userData?.onboarding_steps;

            if (step === 0) {
              navigate("/create#google_auth", {
                state: { name: decoded?.name },
                replace: true,
              });
            } else if (step === 7) {
              navigate("/onboard/upload", { replace: true });
            } else if (step <= 7) {
              navigate(`/onboard#${step}`, { replace: true });
            } else {
              navigate("/explore", { replace: true });
            }
          },
          onError: (error) => {
            AlertNotification("error", "Google login failed");
          },
        },
      );
    } catch (err) {
      console.error("JWT decode error:", err);
    }
  };

  const getInputPlaceholder = () => {
    switch (loginMethod) {
      case "phone":
        return "Enter phone number";
      case "email":
        return "Enter email address";
      case "aec":
        return "Enter AEC ID";
      default:
        return "";
    }
  };

  const getInputLabel = () => {
    switch (loginMethod) {
      case "phone":
        return "Phone Number";
      case "email":
        return "Email ID";
      case "aec":
        return "AEC ID";
      default:
        return "Identifier";
    }
  };

  const getValidationRules = () => {
    const baseRules = [
      {
        required: true,
        message: `Please enter your ${
          loginMethod === "aec"
            ? "AEC ID"
            : loginMethod === "email"
              ? "email"
              : "phone number"
        }`,
      },
    ];

    if (loginMethod === "phone") {
      baseRules.push({
        pattern: /^[0-9]+$/,
        message: "Please enter a valid phone number (numbers only)",
      });

      if (countryCode?.phone_length) {
        baseRules.push({
          validator: (_, value) => {
            if (value && value.length !== countryCode.phone_length) {
              return Promise.reject(
                new Error(
                  `Phone number should be ${countryCode.phone_length} digits`,
                ),
              );
            }
            return Promise.resolve();
          },
        });
      }
    } else if (loginMethod === "email") {
      baseRules.push({
        type: "email",
        message: "Please enter a valid email address",
      });
    } else if (loginMethod === "aec") {
      baseRules.push({
        pattern: /^[a-zA-Z0-9]*$/,
        message: "AEC ID should not contain special characters",
      });
    }

    return baseRules;
  };

  return (
    <AuthLayout>
      <div className="login-wrapper">
        <div className="brand-logo">
          <img className="logo" src={BrandLogo} alt="Logo" />
        </div>

        <div className="page-heading">
          <h2 className="title font-title-xl mb-2">Welcome back</h2>
          <p className="subtitle font-body-xs">Log in with your credentials.</p>
        </div>

        <div className="auth-form-container">
          <Form
            form={form}
            className="login-form"
            onFinish={onFinish}
            layout="vertical"
          >
            <Form.Item className="mb-4" style={{ marginBottom: 0 }}>
              <div className="login-bundle-box">
                <div className="login-method-selector">
                  <RadioButton
                    value={loginMethod}
                    className="radio-button-label"
                    radioGroupClass="radio-group"
                    options={[
                      { label: "Phone number", value: "phone" },
                      { label: "Email ID", value: "email" },
                      { label: "AEC ID", value: "aec" },
                    ]}
                    onChange={(e) => {
                      setLoginMethod(e.target.value);
                      form.setFieldsValue({ identifier: "" });
                    }}
                  />
                </div>

                <div className="login-fields-group">
                  {loginMethod === "phone" && (
                    <div className="country-code-select">
                      <Select
                        // showSearch={false}
                        value={countryCode}
                        options={countryCodesOptions}
                        inputClassName="user-onboarding-select"
                        onChange={(value) => {
                          const selected = countryCodesOptions.find(
                            (item) => item.value === value,
                          );
                          setCountryCode(selected);
                          form.setFieldsValue({ identifier: "" });
                        }}
                        allowClear={false}
                      />
                    </div>
                  )}

                  <div
                    className={`custom-error input-field ${
                      loginMethod === "phone" ? "phone-input-padding" : ""
                    }`}
                    style={{ marginBottom: 0 }}
                  >
                    <Form.Item
                      name="identifier"
                      noStyle
                      rules={getValidationRules()}
                    >
                      <Input
                        id="identifier"
                        label={getInputLabel()}
                        labelClass="label"
                        placeholder={getInputPlaceholder()}
                        type={
                          loginMethod === "email"
                            ? "mail"
                            : loginMethod === "phone"
                              ? "number"
                              : "text"
                        }
                        maxLength={
                          loginMethod === "phone" && countryCode?.phone_length
                            ? countryCode.phone_length
                            : undefined
                        }
                        onChange={(e) => {
                          let value = e.target.value;
                          if (loginMethod === "phone")
                            value = value.replace(/\D/g, "");
                          else if (loginMethod === "aec")
                            value = value.replace(/[^a-zA-Z0-9]/g, "");
                          form.setFieldsValue({ identifier: value });
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Form.Item>

            <Form.Item
              name="password"
              className="custom-error"
              rules={[
                { required: true, message: "Please enter your password!" },
                {
                  min: 6,
                  message: "Password must be at least 6 characters long!",
                },
              ]}
            >
              <Password
                id="password"
                label="Password"
                labelClass="label"
                className="mt-6"
                placeholder=""
                showForgot={true}
              />
            </Form.Item>

            <Button
              loading={isPending}
              htmlType="submit"
              className="mt-8 mb-6"
              wFull
            >
              Log in
            </Button>
          </Form>

          <span className="register-text font-body-xs">
            Not a member?{" "}
            <Button
              onClick={() => navigate("/")}
              className="register-link"
              variant="link"
            >
              Register now
            </Button>
          </span>
        </div>

        <Divider className="divider">or</Divider>

        <div className="login-with-social">
          <Button
            onClick={() => navigate("/login-otp")}
            wFull
            variant="tertiary"
          >
            Log in using OTP
          </Button>

          <div className="custom-google-button-container">
            <Button
              wFull
              variant="tertiary"
              className="custom-google-button"
              icon={<GoogleColored />}
            >
              Sign in with Google
            </Button>
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse?.credential)
                  handleGoogleLogin(credentialResponse.credential);
              }}
              onError={() => console.error("Google login failed")}
              useOneTap={false}
            />
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

export default Login;
